import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ROUTES } from '../../utils/constants';
import { extractAssertionJwt, getDefaultRouteForUser } from '../../utils/lmsLaunch';
import {
  LMS_MISSING_TOKEN_PRESENTATION,
  type LmsLaunchErrorPresentation,
} from '../../utils/lmsLaunchErrors';
import { runLmsLaunchFlow } from '../../integrations/banyanpro/launch/launchFlow';
import { stripAssertionFromUrl } from '../../integrations/banyanpro/security/stripAssertionFromUrl';

type LaunchPhase = 'validating' | 'error';

/**
 * LMS launch bootstrap via gateway POST /auth/authenticate.
 * Primary: /lms/launch?token=<jwt>
 * Legacy: assertion_jwt, assertion
 */
export const LmsLaunchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeLmsSession, user, isAuthenticated } = useAuth();
  const [phase, setPhase] = useState<LaunchPhase>('validating');
  const [errorPresentation, setErrorPresentation] = useState<LmsLaunchErrorPresentation>(
    LMS_MISSING_TOKEN_PRESENTATION
  );
  const launchAttemptedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);

  const returnUrlParam = searchParams.get('returnUrl') ?? searchParams.get('return_url');

  const runLaunch = useCallback(async () => {
    // Capture token in stable ref before mutating URL
    const assertion = extractAssertionJwt(searchParams);
    tokenRef.current = assertion;
    if (process.env.NODE_ENV === 'development') {
      console.debug('[LMS Launch] token found', Boolean(tokenRef.current), 'length', tokenRef.current?.length);
    }
    if (!tokenRef.current) {
      setErrorPresentation(LMS_MISSING_TOKEN_PRESENTATION);
      setPhase('error');
      return;
    }

    // Strip token from URL after capture
    stripAssertionFromUrl();

    const result = await runLmsLaunchFlow(tokenRef.current, {
      returnUrl: returnUrlParam,
      completeLmsSession,
    });
    if (result.ok) {
      navigate(result.targetPath, { replace: true });
    } else {
      setErrorPresentation(result.presentation);
      setPhase('error');
    }
  }, [searchParams, completeLmsSession, returnUrlParam, navigate]);

  useEffect(() => {
    if (launchAttemptedRef.current) return;
    launchAttemptedRef.current = true;
    void runLaunch();
  }, [runLaunch]);

  const handleRetry = () => {
    // Use captured token if available, otherwise try to read once
    if (!tokenRef.current) {
      tokenRef.current = extractAssertionJwt(searchParams);
    }
    if (!tokenRef.current) {
      setErrorPresentation(LMS_MISSING_TOKEN_PRESENTATION);
      setPhase('error');
      return;
    }
    setPhase('validating');
    void runLaunch();
  };

  if (phase === 'validating') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex flex-col items-center gap-4">
            <Spinner size="lg" />
            <p className="text-center text-slate-600">Validating LMS session...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-slate-900">{errorPresentation.title}</h1>
        <p className="mt-2 text-sm text-slate-600">{errorPresentation.message}</p>
        {errorPresentation.supportHint && (
          <p className="mt-3 text-xs text-slate-500">{errorPresentation.supportHint}</p>
        )}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          {errorPresentation.canRetry && (
            <Button type="button" onClick={handleRetry}>
              Try again
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
          >
            Go to sign in
          </Button>
        </div>
        {isAuthenticated && user && (
          <p className="mt-4 text-xs text-slate-500">
            You may already be signed in.{' '}
            <button
              type="button"
              className="text-app-primary underline"
              onClick={() => navigate(getDefaultRouteForUser(user), { replace: true })}
            >
              Continue to your dashboard
            </button>
          </p>
        )}
      </div>
    </div>
  );
};
